import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const LOGO_URL = '/assets/hero/logo.png';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50">
      <Helmet>
        <title>Privacy Policy | Ourmend</title>
        <meta name="description" content="Ourmend Privacy Policy - how we collect, use, and protect your personal information." />
      </Helmet>
      {/* Navbar */}
      <nav className="bg-white/80 backdrop-blur-lg border-b border-stone-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
            <img src={LOGO_URL} alt="Ourmend Logo" className="h-12 w-12 object-contain" />
            <div>
              <span className="text-2xl font-bold" style={{color: '#8B4513'}}>Ourmend</span>
              <p className="text-xs text-stone-600 font-bold">Community Healers. Cultural Care.</p>
            </div>
          </div>
          <Button variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-bold mb-8" style={{color: '#3E2723'}}>Privacy Policy</h1>
        <p className="text-sm text-gray-600 mb-8">Last Updated: {new Date().toLocaleDateString()}</p>

        <div className="prose max-w-none space-y-6 text-gray-700">
          <section>
            <h2 className="text-2xl font-bold mb-4" style={{color: '#8B4513'}}>1. Introduction</h2>
            <p>
              Welcome to Ourmend ("we," "our," or "us"). We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4" style={{color: '#8B4513'}}>2. Information We Collect</h2>
            <p className="font-semibold mb-2">Personal Information:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Name, email address, and phone number</li>
              <li>Payment information (processed securely through Stripe)</li>
              <li>Profile information (for healers: credentials, specialties, location)</li>
              <li>Booking and appointment details</li>
              <li>Communications with healers or our support team</li>
            </ul>

            <p className="font-semibold mb-2 mt-4">Automatically Collected Information:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Device and browser information</li>
              <li>IP address and location data</li>
              <li>Usage data and analytics</li>
              <li>Cookies and similar tracking technologies</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4" style={{color: '#8B4513'}}>3. How We Use Your Information</h2>
            <p>We use your information to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Facilitate bookings between patients and healers</li>
              <li>Process payments securely</li>
              <li>Send booking confirmations and appointment reminders</li>
              <li>Improve and personalize your experience</li>
              <li>Provide customer support</li>
              <li>Prevent fraud and enhance security</li>
              <li>Comply with legal obligations</li>
              <li>Communicate updates and promotional content (with your consent)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4" style={{color: '#8B4513'}}>4. Information Sharing</h2>
            <p>We may share your information with:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Healers:</strong> When you book an appointment, we share necessary information (name, contact details, booking notes) with the healer</li>
              <li><strong>Service Providers:</strong> Third-party vendors who assist with payment processing (Stripe), hosting, analytics, and other services</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
              <li><strong>Business Transfers:</strong> In connection with mergers, acquisitions, or asset sales</li>
            </ul>
            <p className="mt-4">
              We do not sell your personal information to third parties.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4" style={{color: '#8B4513'}}>5. Data Security</h2>
            <p>
              We implement appropriate technical and organizational security measures to protect your personal information. However, no electronic transmission or storage is 100% secure. While we strive to protect your data, we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4" style={{color: '#8B4513'}}>6. Your Rights</h2>
            <p>You have the right to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Access, update, or delete your personal information</li>
              <li>Object to or restrict certain processing of your data</li>
              <li>Withdraw consent where processing is based on consent</li>
              <li>Request a copy of your data</li>
              <li>Lodge a complaint with a supervisory authority</li>
            </ul>
            <p className="mt-4">
              To exercise these rights, please contact us at kgracedevelopment@gmail.com.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4" style={{color: '#8B4513'}}>7. Cookies and Tracking</h2>
            <p>
              We use cookies and similar technologies to enhance your experience, analyze usage, and provide personalized content. You can control cookie settings through your browser preferences.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4" style={{color: '#8B4513'}}>8. Third-Party Links</h2>
            <p>
              Our platform may contain links to third-party websites. We are not responsible for the privacy practices of these external sites. We encourage you to read their privacy policies.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4" style={{color: '#8B4513'}}>9. Children's Privacy</h2>
            <p>
              Our services are not intended for individuals under 18 years of age. We do not knowingly collect information from children. If you believe we have collected information from a child, please contact us immediately.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4" style={{color: '#8B4513'}}>10. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of significant changes by posting the new policy on this page and updating the "Last Updated" date.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4" style={{color: '#8B4513'}}>11. Contact Us</h2>
            <p>
              If you have questions or concerns about this Privacy Policy, please contact us:
            </p>
            <ul className="list-none space-y-1 mt-4">
              <li><strong>Email:</strong> kgracedevelopment@gmail.com</li>
              <li><strong>Platform:</strong> Ourmend</li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;